import { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import SceneLayout from '@/components/scene/SceneLayout';
import Aperture from '@/components/scene/Aperture';

// A building directory: the requested room isn't on the plan, so the board lists the ones that are.
const DIRECTORY = [
  { code: 'G', key: 'home', to: '/' },
  { code: '01', key: 'pricing', to: '/pricing' },
  { code: '02', key: 'how_it_works', to: '/how-it-works' },
  { code: '03', key: 'about', to: '/about' },
  { code: '04', key: 'support', to: '/support' },
] as const;

export default function NotFound() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.error('404 Error: User attempted to access non-existent route:', location.pathname);
  }, [location.pathname]);

  return (
    <SceneLayout>
      <section className="wrap py-[clamp(32px,6vh,72px)]">
        <Aperture ticks rv="aperture" label={t('notFound.label')} className="notfound">
          <div className="notfound__copy">
            <p className="mono">
              {t('notFound.requested')} · <span dir="ltr" className="normal-case">{location.pathname}</span>
            </p>
            <p className="giant" aria-hidden="true">{t('notFound.code')}</p>
            <div className="grid gap-4">
              <h1 className="display-m">{t('notFound.title')}</h1>
              <p className="body max-w-[40ch]">{t('notFound.subtitle')}</p>
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
              <button type="button" className="btn-ink" onClick={() => navigate('/')}>
                {t('notFound.cta')}
                <ArrowRight className="btn-arrow h-4 w-4" />
              </button>
              <Link to="/support" className="link-line">{t('notFound.support_cta')}</Link>
            </div>
          </div>

          <nav className="notfound__board" aria-label={t('notFound.directory_title')}>
            <p className="mono mb-6">{t('notFound.directory_title')}</p>
            <ul data-rv="line">
              <li className="ledger__head" aria-hidden="true" />
              {DIRECTORY.map((room, i) => (
                <li key={room.key} className="relative">
                  <Link to={room.to} className="notfound__room">
                    <span className="mono">{room.code}</span>
                    <span className="text-[17px] font-medium">{t(`nav.${room.key}`)}</span>
                    <span className="body text-[14px]">{t(`notFound.directory.${room.key}`)}</span>
                  </Link>
                  <span className="rv-line" style={{ ['--i' as string]: i }} />
                </li>
              ))}
            </ul>
          </nav>
        </Aperture>
      </section>
    </SceneLayout>
  );
}
